import React, { useState, useEffect, useRef } from 'react';
import {
  Beaker,
  Users,
  MessageSquare,
  CheckSquare,
  FolderOpen,
  Briefcase,
  Clock,
  DollarSign,
  Send,
  BookOpen,
  Calendar,
  FileText,
  Link2,
  Code,
  Database,
  Star,
  Filter,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import './PlatformShowcase.css';

/**
 * PlatformShowcase - Overlapping Cards Component
 * Card 1 (Top): Research Labs – 5 tabs matching real module
 * Card 2 (Bottom): Research Freelance marketplace
 */
const PlatformShowcase = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeCard, setActiveCard] = useState(0);
  const [labActiveTab, setLabActiveTab] = useState('chat');
  const [flActiveFilter, setFlActiveFilter] = useState('all');
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // Auto-rotate lab tabs through all 5
  useEffect(() => {
    if (!isVisible) return;
    const tabs = ['chat', 'tasks', 'files', 'library', 'calendar'];
    let idx = 0;
    const interval = setInterval(() => {
      idx = (idx + 1) % tabs.length;
      setLabActiveTab(tabs[idx]);
    }, 4000);
    return () => clearInterval(interval);
  }, [isVisible]);

  // ========================================
  // LAB DATA
  // ========================================
  const labMembers = [
    { initials: 'LH', name: 'Dr. Lena Hoffmann', role: 'Lead', color: '#374151' },
    { initials: 'IM', name: 'Prof. Idris Malik', role: 'Co-Lead', color: '#06b6d4' },
    { initials: 'FA', name: 'Farid Anwar', role: 'Member', color: '#6b7280' },
    { initials: 'AR', name: 'Dr. Amina Rashid', role: 'Member', color: '#f59e0b' },
    { initials: 'MA', name: 'Mehran Akhtar', role: 'Member', color: '#ec4899' },
  ];

  const chatMessages = [
    { user: 'Dr. Lena Hoffmann', initials: 'LH', color: '#374151', msg: 'Uploaded the revised alignment benchmark results to Files.', time: '10:32 AM', isMe: false },
    { user: 'Farid Anwar', initials: 'FA', color: '#6b7280', msg: 'Looks solid. The reward hacking metric dropped 18% after tuning.', time: '10:45 AM', isMe: false },
    { user: 'You', initials: 'MA', color: '#ec4899', msg: 'Great progress. I will run the ablation study this afternoon.', time: '10:51 AM', isMe: true },
    { user: 'Prof. Idris Malik', initials: 'IM', color: '#06b6d4', msg: 'Let us sync at 3 PM to review everything before submission.', time: '11:03 AM', isMe: false },
  ];

  // Kanban-style columns matching the real module
  const kanbanColumns = [
    {
      id: 'todo',
      label: 'To Do',
      color: '#374151',
      bg: '#eef2ff',
      tasks: [
        { title: 'Review related work refs', assignee: 'AR', priority: 'low' },
      ],
    },
    {
      id: 'in-progress',
      label: 'In Progress',
      color: '#f59e0b',
      bg: '#fffbeb',
      tasks: [
        { title: 'Run ablation study on RLHF', assignee: 'MA', priority: 'high' },
        { title: 'Write methodology section', assignee: 'LH', priority: 'high' },
      ],
    },
    {
      id: 'review',
      label: 'Review',
      color: '#0284c7',
      bg: '#f0f9ff',
      tasks: [
        { title: 'Benchmark comparison tables', assignee: 'FA', priority: 'medium' },
      ],
    },
    {
      id: 'completed',
      label: 'Completed',
      color: '#6b7280',
      bg: '#ecfdf5',
      tasks: [
        { title: 'Set up evaluation server', assignee: 'IM', priority: 'medium' },
        { title: 'Initial dataset preprocessing', assignee: 'AR', priority: 'low' },
      ],
    },
  ];

  const files = [
    { name: 'alignment_benchmark_v3.pdf', type: 'pdf', size: '2.4 MB', updated: '2h ago' },
    { name: 'rlhf_training_logs.csv', type: 'data', size: '18.7 MB', updated: '5h ago' },
    { name: 'methodology_draft.docx', type: 'doc', size: '340 KB', updated: '1d ago' },
    { name: 'reward_model_weights.pt', type: 'model', size: '1.2 GB', updated: '3d ago' },
    { name: 'figures/', type: 'folder', size: '12 items', updated: '6h ago' },
  ];

  // Library resources matching the real module categories
  const libraryResources = [
    { title: 'Constitutional AI: Harmlessness from AI Feedback', type: 'paper', author: 'Bai et al.', icon: FileText, color: '#3b82f6', citations: 342 },
    { title: 'RLHF Implementation Notes', type: 'note', author: 'Dr. Lena Hoffmann', icon: BookOpen, color: '#6b7280', citations: null },
    { title: 'reward_hacking_detector.py', type: 'code', author: 'Farid Anwar', icon: Code, color: '#f59e0b', citations: null },
    { title: 'Safety Evaluation Dataset v2', type: 'dataset', author: 'Team', icon: Database, color: '#0284c7', citations: null },
    { title: 'https://arxiv.org/abs/2302.08582', type: 'link', author: 'External', icon: Link2, color: '#0ea5e9', citations: null },
  ];

  // Calendar events matching the real module event types
  const calendarDays = [
    { day: 1, events: [] }, { day: 2, events: [] },
    { day: 3, events: [{ type: 'meeting', color: '#3b82f6' }] },
    { day: 4, events: [] }, { day: 5, events: [] },
    { day: 6, events: [{ type: 'deadline', color: '#ef4444' }] },
    { day: 7, events: [] }, { day: 8, events: [] },
    { day: 9, events: [{ type: 'presentation', color: '#0284c7' }] },
    { day: 10, events: [] }, { day: 11, events: [] },
    { day: 12, events: [{ type: 'discussion', color: '#f59e0b' }, { type: 'workshop', color: '#6b7280' }] },
    { day: 13, events: [] }, { day: 14, events: [] },
    { day: 15, events: [{ type: 'milestone', color: '#ec4899' }] },
    { day: 16, events: [] }, { day: 17, events: [] },
    { day: 18, events: [{ type: 'meeting', color: '#3b82f6' }] },
    { day: 19, events: [] }, { day: 20, events: [] },
    { day: 21, events: [{ type: 'deadline', color: '#ef4444' }] },
    { day: 22, events: [] }, { day: 23, events: [] },
    { day: 24, events: [] }, { day: 25, events: [{ type: 'reminder', color: '#06b6d4' }] },
    { day: 26, events: [] }, { day: 27, events: [] }, { day: 28, events: [] },
  ];

  const upcomingEvents = [
    { title: 'Weekly Lab Sync', type: 'meeting', date: 'Feb 6, 3:00 PM', color: '#3b82f6', attendees: 5 },
    { title: 'Paper Submission Deadline', type: 'deadline', date: 'Feb 6, 11:59 PM', color: '#ef4444', attendees: null },
    { title: 'Methodology Presentation', type: 'presentation', date: 'Feb 9, 2:00 PM', color: '#0284c7', attendees: 8 },
    { title: 'RLHF Workshop Session', type: 'workshop', date: 'Feb 12, 10:00 AM', color: '#6b7280', attendees: 12 },
  ];

  // ========================================
  // FREELANCE DATA
  // ========================================
  const flCategories = [
    { key: 'all', label: 'All' },
    { key: 'paid', label: 'Paid' },
    { key: 'volunteer', label: 'Volunteer' },
    { key: 'coauthor', label: 'Co-authorship' },
  ];

  const freelanceGigs = [
    {
      title: 'Statistical Analysis for Phase III Clinical Trial',
      poster: 'Dr. Brigitte Wenger',
      posterInitials: 'BW',
      posterColor: '#374151',
      institution: 'Mayo Clinic',
      rating: 4.8,
      budget: '$800 – $1,200',
      deadline: '15 days',
      skills: ['R', 'Survival Analysis', 'SAS'],
      applicants: 12,
      maxApplicants: 20,
      isUrgent: true,
      paymentType: 'paid',
      researchArea: 'Medicine',
    },
    {
      title: 'ML Model for Protein Folding Prediction',
      poster: 'Prof. Idris Malik',
      posterInitials: 'IM',
      posterColor: '#06b6d4',
      institution: 'MIT Biology',
      rating: 4.9,
      budget: '$2,500 – $4,000',
      deadline: '30 days',
      skills: ['PyTorch', 'Bioinformatics'],
      applicants: 8,
      maxApplicants: 15,
      isUrgent: false,
      paymentType: 'paid',
      researchArea: 'AI/ML',
    },
    {
      title: 'Literature Review on Quantum Computing',
      poster: 'Dr. Mei-Lin Chou',
      posterInitials: 'MC',
      posterColor: '#0284c7',
      institution: 'Caltech Physics',
      rating: 4.6,
      budget: 'Co-authorship',
      deadline: '45 days',
      skills: ['Quantum Mechanics', 'LaTeX'],
      applicants: 5,
      maxApplicants: 10,
      isUrgent: false,
      paymentType: 'coauthor',
      researchArea: 'Physics',
    },
    {
      title: 'Interactive Climate Data Dashboard',
      poster: 'Dr. Tobias Richter',
      posterInitials: 'TR',
      posterColor: '#6b7280',
      institution: 'NOAA Research',
      rating: 4.7,
      budget: 'Volunteer',
      deadline: '10 days',
      skills: ['D3.js', 'Python', 'Plotly'],
      applicants: 15,
      maxApplicants: 20,
      isUrgent: true,
      paymentType: 'volunteer',
      researchArea: 'Environment',
    },
  ];

  const filteredGigs = flActiveFilter === 'all'
    ? freelanceGigs
    : freelanceGigs.filter((g) => g.paymentType === flActiveFilter);

  const freelanceStats = [
    { label: 'Active Gigs', value: '1,247', trend: '+42' },
    { label: 'Avg. Budget', value: '$1,450', trend: '+8%' },
    { label: 'Completed', value: '8,320', trend: '+156' },
    { label: 'Researchers', value: '3,240', trend: '+89' },
  ];

  // ========================================
  // RENDER: Lab Card Content
  // ========================================
  const labTabs = [
    { key: 'chat', icon: MessageSquare, label: 'Chat', color: '#3b82f6' },
    { key: 'tasks', icon: CheckSquare, label: 'Tasks', color: '#6b7280' },
    { key: 'files', icon: FolderOpen, label: 'Files', color: '#f59e0b' },
    { key: 'library', icon: BookOpen, label: 'Library', color: '#0284c7' },
    { key: 'calendar', icon: Calendar, label: 'Calendar', color: '#ec4899' },
  ];

  const renderLabCard = () => (
    <div className="ps-lab">
      {/* Lab Header */}
      <div className="ps-lab__header">
        <div className="ps-lab__header-left">
          <div className="ps-lab__icon">
            <Beaker size={14} />
          </div>
          <div>
            <h3 className="ps-lab__name">AI Safety Research Lab</h3>
            <span className="ps-lab__meta">5 members · Active now</span>
          </div>
        </div>
        <div className="ps-lab__members-stack">
          {labMembers.slice(0, 4).map((m, i) => (
            <div
              key={i}
              className="ps-lab__member-dot"
              style={{ background: m.color, zIndex: 5 - i }}
              title={m.name}
            >
              {m.initials}
            </div>
          ))}
          <div className="ps-lab__member-dot ps-lab__member-more">+1</div>
        </div>
      </div>

      {/* Lab Tabs — 5 tabs matching real RoomDetail */}
      <div className="ps-lab__tabs">
        {labTabs.map((tab) => (
          <button
            key={tab.key}
            className={`ps-lab__tab ${labActiveTab === tab.key ? 'ps-lab__tab--active' : ''}`}
            onClick={(e) => { e.stopPropagation(); setLabActiveTab(tab.key); }}
            style={labActiveTab === tab.key ? { color: tab.color, borderBottomColor: tab.color } : {}}
          >
            <tab.icon size={10} />
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="ps-lab__content">
        {/* ===== CHAT TAB ===== */}
        {labActiveTab === 'chat' && (
          <div className="ps-lab__chat">
            <div className="ps-lab__chat-pinned">
              <MessageSquare size={9} />
              <span>Pinned: Paper submission deadline — Feb 6, 11:59 PM</span>
            </div>
            {chatMessages.map((msg, i) => (
              <div key={i} className={`ps-lab__msg ${msg.isMe ? 'ps-lab__msg--me' : ''}`}>
                {!msg.isMe && (
                  <div className="ps-lab__msg-avatar" style={{ background: msg.color }}>
                    {msg.initials}
                  </div>
                )}
                <div className="ps-lab__msg-body">
                  {!msg.isMe && <span className="ps-lab__msg-name">{msg.user}</span>}
                  <p className="ps-lab__msg-text">{msg.msg}</p>
                  <span className="ps-lab__msg-time">{msg.time}</span>
                </div>
              </div>
            ))}
            <div className="ps-lab__chat-input">
              <span className="ps-lab__input-placeholder">Type a message...</span>
              <Send size={12} className="ps-lab__send-icon" />
            </div>
          </div>
        )}

        {/* ===== TASKS TAB — Kanban Board ===== */}
        {labActiveTab === 'tasks' && (
          <div className="ps-lab__kanban">
            {kanbanColumns.map((col) => (
              <div key={col.id} className="ps-lab__kanban-col">
                <div className="ps-lab__kanban-col-header">
                  <span className="ps-lab__kanban-col-dot" style={{ background: col.color }} />
                  <span className="ps-lab__kanban-col-title">{col.label}</span>
                  <span className="ps-lab__kanban-col-count">{col.tasks.length}</span>
                </div>
                <div className="ps-lab__kanban-col-body">
                  {col.tasks.map((task, ti) => (
                    <div key={ti} className="ps-lab__kanban-card" style={{ borderLeftColor: col.color }}>
                      <span className="ps-lab__kanban-card-title">{task.title}</span>
                      <div className="ps-lab__kanban-card-footer">
                        <span className="ps-lab__kanban-card-assignee">{task.assignee}</span>
                        <span className={`ps-lab__kanban-card-priority ps-lab__kanban-card-priority--${task.priority}`}>
                          {task.priority}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ===== FILES TAB ===== */}
        {labActiveTab === 'files' && (
          <div className="ps-lab__files">
            <div className="ps-lab__files-toolbar">
              <div className="ps-lab__files-type-filters">
                {['All', 'PDFs', 'Data', 'Docs'].map((f) => (
                  <span key={f} className={`ps-lab__files-type-pill ${f === 'All' ? 'ps-lab__files-type-pill--active' : ''}`}>{f}</span>
                ))}
              </div>
              <span className="ps-lab__files-count">{files.length} files</span>
            </div>
            {files.map((file, i) => (
              <div key={i} className="ps-lab__file">
                <div className={`ps-lab__file-icon ps-lab__file-icon--${file.type}`}>
                  {file.type === 'folder' ? <FolderOpen size={12} /> : <FileText size={12} />}
                </div>
                <div className="ps-lab__file-info">
                  <span className="ps-lab__file-name">{file.name}</span>
                  <span className="ps-lab__file-meta">{file.size} · {file.updated}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ===== LIBRARY TAB ===== */}
        {labActiveTab === 'library' && (
          <div className="ps-lab__library">
            <div className="ps-lab__lib-header-bar">
              <span className="ps-lab__lib-count">{libraryResources.length} resources</span>
              <div className="ps-lab__lib-type-filters">
                {['All', 'Papers', 'Notes', 'Code', 'Data'].map((f) => (
                  <span key={f} className={`ps-lab__lib-pill ${f === 'All' ? 'ps-lab__lib-pill--active' : ''}`}>{f}</span>
                ))}
              </div>
            </div>
            {libraryResources.map((res, i) => (
              <div key={i} className="ps-lab__lib-item">
                <div className="ps-lab__lib-item-icon" style={{ background: `${res.color}15`, color: res.color }}>
                  <res.icon size={12} />
                </div>
                <div className="ps-lab__lib-item-info">
                  <span className="ps-lab__lib-item-title">{res.title}</span>
                  <div className="ps-lab__lib-item-meta">
                    <span className="ps-lab__lib-item-type" style={{ color: res.color }}>{res.type}</span>
                    <span className="ps-lab__lib-item-author">by {res.author}</span>
                    {res.citations && <span className="ps-lab__lib-item-cites">{res.citations} citations</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ===== CALENDAR TAB ===== */}
        {labActiveTab === 'calendar' && (
          <div className="ps-lab__calendar">
            {/* Mini month view */}
            <div className="ps-lab__cal-header">
              <button className="ps-lab__cal-nav" onClick={(e) => e.stopPropagation()}>
                <ChevronLeft size={12} />
              </button>
              <span className="ps-lab__cal-month">Feb 2026</span>
              <button className="ps-lab__cal-nav" onClick={(e) => e.stopPropagation()}>
                <ChevronRight size={12} />
              </button>
            </div>
            <div className="ps-lab__cal-grid">
              <div className="ps-lab__cal-weekdays">
                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
                  <span key={i} className="ps-lab__cal-weekday">{d}</span>
                ))}
              </div>
              <div className="ps-lab__cal-days">
                {calendarDays.map((d) => (
                  <div
                    key={d.day}
                    className={`ps-lab__cal-day ${d.day === 6 ? 'ps-lab__cal-day--today' : ''} ${d.events.length > 0 ? 'ps-lab__cal-day--has-event' : ''}`}
                  >
                    <span className="ps-lab__cal-day-num">{d.day}</span>
                    {d.events.length > 0 && (
                      <div className="ps-lab__cal-day-dots">
                        {d.events.map((ev, ei) => (
                          <span key={ei} className="ps-lab__cal-event-dot" style={{ background: ev.color }} />
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Upcoming events */}
            <div className="ps-lab__cal-upcoming">
              <span className="ps-lab__cal-upcoming-title">Upcoming</span>
              {upcomingEvents.slice(0, 3).map((ev, i) => (
                <div key={i} className="ps-lab__cal-event">
                  <span className="ps-lab__cal-event-bar" style={{ background: ev.color }} />
                  <div className="ps-lab__cal-event-info">
                    <span className="ps-lab__cal-event-name">{ev.title}</span>
                    <span className="ps-lab__cal-event-date">{ev.date}</span>
                  </div>
                  <span className="ps-lab__cal-event-type" style={{ background: `${ev.color}15`, color: ev.color }}>
                    {ev.type}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // ========================================
  // RENDER: Freelance Card Content
  // ========================================
  const renderFreelanceCard = () => (
    <div className="ps-fl">
      {/* Freelance Header */}
      <div className="ps-fl__header">
        <div className="ps-fl__header-left">
          <div className="ps-fl__icon">
            <Briefcase size={14} />
          </div>
          <div>
            <h3 className="ps-fl__title">Research Marketplace</h3>
            <span className="ps-fl__subtitle">Find experts or offer your skills</span>
          </div>
        </div>
        <div className="ps-fl__header-right">
          <Filter size={11} />
        </div>
      </div>

      {/* Stats Row */}
      <div className="ps-fl__stats">
        {freelanceStats.map((stat, i) => (
          <div key={i} className="ps-fl__stat">
            <span className="ps-fl__stat-value">{stat.value}</span>
            <span className="ps-fl__stat-label">{stat.label}</span>
            <span className="ps-fl__stat-trend">+{stat.trend.replace('+', '')}</span>
          </div>
        ))}
      </div>

      {/* Payment Type Filter Tabs */}
      <div className="ps-fl__filters">
        {flCategories.map((cat) => (
          <button
            key={cat.key}
            className={`ps-fl__filter-btn ${flActiveFilter === cat.key ? 'ps-fl__filter-btn--active' : ''}`}
            onClick={(e) => { e.stopPropagation(); setFlActiveFilter(cat.key); }}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Gig Listings */}
      <div className="ps-fl__gigs">
        {filteredGigs.slice(0, 3).map((gig, i) => (
          <div key={i} className="ps-fl__gig">
            <div className="ps-fl__gig-top">
              <h4 className="ps-fl__gig-title">{gig.title}</h4>
              <div className="ps-fl__gig-badges">
                {gig.isUrgent && <span className="ps-fl__gig-urgent">Urgent</span>}
                <span className={`ps-fl__gig-payment ps-fl__gig-payment--${gig.paymentType}`}>
                  {gig.paymentType === 'paid' ? 'Paid' : gig.paymentType === 'volunteer' ? 'Volunteer' : 'Co-author'}
                </span>
              </div>
            </div>
            <div className="ps-fl__gig-poster">
              <div className="ps-fl__gig-avatar" style={{ background: gig.posterColor }}>
                {gig.posterInitials}
              </div>
              <span className="ps-fl__gig-poster-name">{gig.poster}</span>
              <span className="ps-fl__gig-poster-inst">· {gig.institution}</span>
              <span className="ps-fl__gig-rating">
                <Star size={8} />
                {gig.rating}
              </span>
            </div>
            <div className="ps-fl__gig-details">
              <span className="ps-fl__gig-budget">
                <DollarSign size={10} />
                {gig.budget}
              </span>
              <span className="ps-fl__gig-deadline">
                <Clock size={10} />
                {gig.deadline}
              </span>
              <span className="ps-fl__gig-area">{gig.researchArea}</span>
            </div>
            <div className="ps-fl__gig-bottom">
              <div className="ps-fl__gig-skills">
                {gig.skills.map((skill, j) => (
                  <span key={j} className="ps-fl__skill-tag">{skill}</span>
                ))}
              </div>
              <div className="ps-fl__gig-applicant-bar">
                <div className="ps-fl__gig-applicant-fill" style={{ width: `${(gig.applicants / gig.maxApplicants) * 100}%` }} />
              </div>
              <span className="ps-fl__gig-applicant-text">
                <Users size={9} />
                {gig.applicants}/{gig.maxApplicants} applied
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <section
      ref={sectionRef}
      className={`platform-showcase ${isVisible ? 'platform-showcase--visible' : ''}`}
    >
      <div className="platform-showcase__container">
        {/* Left Column - Text Content */}
        <div className="platform-showcase__text">
          {/* Research Section */}
          <div className="platform-showcase__section">
            <h2 className="platform-showcase__title">Your research, all in one place</h2>
            <p className="platform-showcase__desc">
              Private labs, real-time collaboration, encrypted messaging, and a freelance marketplace — built for how researchers actually work.
            </p>
            <a href="/signup" className="platform-showcase__link">
              Get Started <span className="platform-showcase__arrow">→</span>
            </a>
          </div>

          {/* Marketplace Section */}
          <div className="platform-showcase__section platform-showcase__section--secondary">
            <h3 className="platform-showcase__subtitle">Hire or get hired</h3>
            <p className="platform-showcase__desc platform-showcase__desc--small">
              Post research gigs or offer your expertise. Find statisticians, ML engineers, and domain experts in minutes.
            </p>
            <a href="/freelance" className="platform-showcase__link platform-showcase__link--secondary">
              Browse Marketplace <span className="platform-showcase__arrow">→</span>
            </a>
          </div>

          {/* Courses & Workshops Section */}
          <div className="platform-showcase__section platform-showcase__section--secondary">
            <h3 className="platform-showcase__subtitle">Grow your skills</h3>
            <p className="platform-showcase__desc platform-showcase__desc--small">
              Expert-led workshops and courses on ML, data science, and research methods — learn by doing.
            </p>
            <a href="/resources" className="platform-showcase__link platform-showcase__link--secondary">
              Explore Resources <span className="platform-showcase__arrow">→</span>
            </a>
          </div>
        </div>

        {/* Right Column - Overlapping Cards */}
        <div className="platform-showcase__cards-wrapper">
          <div className="platform-showcase__cards-container">
            {/* Card 1 - Research Labs */}
            <div
              className={`platform-showcase__card platform-showcase__card--top ${activeCard === 0 ? 'active' : ''}`}
              onClick={() => setActiveCard(0)}
            >
              {renderLabCard()}
            </div>

            {/* Card 2 - Freelance Marketplace */}
            <div
              className={`platform-showcase__card platform-showcase__card--bottom ${activeCard === 1 ? 'active' : ''}`}
              onClick={() => setActiveCard(1)}
            >
              {renderFreelanceCard()}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PlatformShowcase;
